import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Menu, X, Calendar, ArrowRight, Sparkles, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import HeroSection from './sections/HeroSection';
import StorySection from './sections/StorySection';
import NewRatesSection from './sections/NewRatesSection';
import TrainingLocationsSection from './sections/TrainingLocationsSection';
import TrainingScheduleSection from './sections/TrainingScheduleSection';
import CommitmentSection from './sections/CommitmentSection';
import ValuePropositionSection from './sections/ValuePropositionSection';
import SummarySection from './sections/SummarySection';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sections = [
  { id: 'hero', title: 'Home' },
  { id: 'story', title: 'Our Story' },
  { id: 'locations', title: 'Locations' },
  { id: 'schedule', title: 'Schedule' },
  { id: 'new-rates', title: 'Rates' },
  { id: 'commitment', title: 'Commitment' },
  { id: 'value', title: 'Why SCC' },
  { id: 'summary', title: 'Summary' },
];

export const PresentationDeck = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCampBanner, setShowCampBanner] = useState(true);
  const [showAcademyCTA, setShowAcademyCTA] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  
  // Dynamic banner — fetches the most imminent published event from Portal
  const [currentEvent, setCurrentEvent] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/events/public`)
      .then((r) => (r.ok ? r.json() : []))
      .then((events) => {
        if (!Array.isArray(events) || events.length === 0) return;
        const today = new Date().toISOString().slice(0, 10);
        // Pick the first event whose registration is open or coming soon
        const active = events.find((e) => {
          if (e.registration_close_date && today > e.registration_close_date) return false;
          if (typeof e.spots_remaining === 'number' && e.spots_remaining <= 0) return false;
          return true;
        });
        if (active) setCurrentEvent(active);
      })
      .catch(() => {});
  }, []);

  const hasSpecialEvents = !!currentEvent;

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const element = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setCurrentSection(i);
          break;
        }
      }
      
      // Show Academy CTA when on schedule, rates, or commitment sections
      const scheduleEl = document.getElementById('schedule');
      const ratesEl = document.getElementById('new-rates');
      const commitmentEl = document.getElementById('commitment');
      const valueEl = document.getElementById('value');
      
      if (scheduleEl && valueEl) {
        const scheduleTop = scheduleEl.offsetTop;
        const valueTop = valueEl.offsetTop;
        const scrollY = window.scrollY + 100;
        
        // Show CTA when scrolling through schedule, rates, commitment sections
        setShowAcademyCTA(scrollY >= scheduleTop && scrollY < valueTop);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (index) => {
    const element = document.getElementById(sections[index].id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const scrollToNext = () => {
    if (currentSection < sections.length - 1) {
      scrollToSection(currentSection + 1);
    }
  };

  const scrollToPrev = () => {
    if (currentSection > 0) {
      scrollToSection(currentSection - 1);
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-background">
      {/* Special Event Banner - Only shows when there are active special events */}
      <AnimatePresence>
        {hasSpecialEvents && showCampBanner && currentEvent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-primary via-yellow-600 to-primary overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <Sparkles className="h-5 w-5 text-black animate-pulse hidden sm:block" />
                <p className="text-black font-medium text-sm sm:text-base">
                  <span className="font-bold">{currentEvent.name}</span>
                  <span className="hidden sm:inline"> • {currentEvent.start_date} • {typeof currentEvent.spots_remaining === 'number' && currentEvent.spots_remaining <= 10 ? `Only ${currentEvent.spots_remaining} spots left!` : 'Limited Spots!'}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate(`/event/${currentEvent.slug}`)}
                  size="sm"
                  className="bg-black text-primary hover:bg-gray-900 font-bold px-4 shadow-lg"
                  data-testid="camp-banner-register-btn"
                >
                  Register Now
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <button
                  onClick={() => setShowCampBanner(false)}
                  className="text-black/70 hover:text-black p-1"
                  aria-label="Close banner"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <nav className={`fixed left-0 right-0 z-50 glass-card px-6 py-4 transition-all duration-300 ${hasSpecialEvents && showCampBanner ? 'top-[52px]' : 'top-0'}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_fc9d91ec-20e9-46cd-af6f-ead15b7d04e2/artifacts/aklhfw0k_SCC%20LOGO.jpeg" 
              alt="SCC Logo" 
              className="h-12 w-12 object-contain rounded"
            />
            <div>
              <h1 className="font-display text-lg text-primary font-semibold">SCC Football Academy</h1>
              <p className="text-xs text-muted-foreground">Youth Football Development</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {sections.map((section, index) => (
              <Button
                key={section.id}
                variant={currentSection === index ? 'default' : 'ghost'}
                size="sm"
                onClick={() => scrollToSection(index)}
                className={currentSection === index 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-primary'}
              >
                {section.title}
              </Button>
            ))}
            {/* Free Trial Button */}
            <Button
              onClick={() => navigate('/free-trial')}
              size="sm"
              variant="outline"
              className="border-green-500 text-green-400 hover:bg-green-500/10 ml-2"
              data-testid="nav-trial-btn"
            >
              Free Trial
            </Button>
            {/* Register Button in Nav */}
            <Button
              onClick={() => navigate('/register')}
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
              data-testid="nav-register-btn"
            >
              <Calendar className="h-4 w-4 mr-1" />
              Register
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-primary"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed left-0 right-0 z-40 glass-card p-4 md:hidden ${hasSpecialEvents && showCampBanner ? 'top-[120px]' : 'top-20'}`}
          >
            <div className="flex flex-col gap-2">
              {/* Free Trial Button - First in mobile menu */}
              <Button
                onClick={() => { navigate('/free-trial'); setIsMenuOpen(false); }}
                variant="outline"
                className="border-green-500 text-green-400 hover:bg-green-500/10 w-full justify-start"
                data-testid="mobile-trial-btn"
              >
                Free Trial
              </Button>
              {/* Register Button */}
              <Button
                onClick={() => { navigate('/register'); setIsMenuOpen(false); }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold w-full justify-start mb-2"
                data-testid="mobile-register-btn"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Register
              </Button>
              {sections.map((section, index) => (
                <Button
                  key={section.id}
                  variant={currentSection === index ? 'default' : 'ghost'}
                  onClick={() => scrollToSection(index)}
                  className={currentSection === index 
                    ? 'bg-primary text-primary-foreground w-full justify-start' 
                    : 'text-muted-foreground hover:text-primary w-full justify-start'}
                >
                  {section.title}
                </Button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Section Progress Indicator */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-3">
        {sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentSection === index 
                ? 'bg-primary scale-125 shadow-glow' 
                : 'bg-muted hover:bg-primary/50'
            }`}
            title={section.title}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-2">
        {currentSection > 0 && (
          <Button
            variant="outline"
            size="icon"
            onClick={scrollToPrev}
            className="gold-border hover:bg-primary/10 text-primary"
          >
            <ChevronUp className="h-5 w-5" />
          </Button>
        )}
        {currentSection < sections.length - 1 && (
          <Button
            variant="outline"
            size="icon"
            onClick={scrollToNext}
            className="gold-border hover:bg-primary/10 text-primary"
          >
            <ChevronDown className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Sticky Academy Registration CTA - Desktop Sidebar */}
      <AnimatePresence>
        {showAcademyCTA && (
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed left-0 top-1/2 -translate-y-1/2 z-40 hidden lg:block"
          >
            <div className="bg-gradient-to-r from-primary to-primary/80 rounded-r-xl shadow-xl overflow-hidden">
              <Button
                onClick={() => navigate('/register-academy')}
                className="bg-transparent hover:bg-white/10 text-primary-foreground px-4 py-8 h-auto flex flex-col items-center gap-2 rounded-none"
                data-testid="sticky-sidebar-academy-btn"
              >
                <Users className="h-6 w-6" />
                <span className="writing-mode-vertical text-sm font-bold whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                  Register for Academy
                </span>
                <ArrowRight className="h-4 w-4 rotate-90 mt-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Academy Registration CTA - Mobile */}
      <AnimatePresence>
        {showAcademyCTA && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-6 left-6 z-40 lg:hidden"
          >
            <Button
              onClick={() => navigate('/register-academy')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold shadow-xl px-4 py-6"
              data-testid="floating-academy-btn"
            >
              <Users className="h-5 w-5 mr-2" />
              Register
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sections */}
      <main>
        <HeroSection />
        <StorySection />
        <TrainingLocationsSection />
        <TrainingScheduleSection />
        <NewRatesSection />
        <CommitmentSection />
        <ValuePropositionSection />
        <SummarySection />
      </main>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-border py-4">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-foreground text-xs">© 2025 SCC Football Academy. All rights reserved.</p>
          <p className="text-muted-foreground text-xs mt-1">www.sccfootball.com | HP 8818 6675</p>
        </div>
      </footer>
    </div>
  );
};

export default PresentationDeck;
